import { Config } from '@/config';
import { ApiService } from '@/services/api.service';
import axios from 'axios';

const CancelToken = axios.CancelToken;
let cancelTokens = {
  opportunityVotes: null,
  opportunityVoteReasons: null,
  finalDecision: null,
  finalDecisionHistory: null,
};

const finalDecisionUrl = (opportunityId) =>
  `${Config.api.document.url}api/v1/opportunities/${opportunityId}/final-decision`;

class GoNoGoService {
  getOpportunityVotes({ opportunityId }) {
    if (cancelTokens.opportunityVotes) cancelTokens.opportunityVotes.cancel('Multiple requests.');
    cancelTokens.opportunityVotes = CancelToken.source();

    return ApiService.get(`${Config.api.document.url}api/v1/opportunities/${opportunityId}/votes`, {
      cancelToken: cancelTokens.opportunityVotes.token,
    }).then((response) => response.data);
  }

  getOpportunityVoteReasons() {
    if (cancelTokens.opportunityVoteReasons) cancelTokens.opportunityVoteReasons.cancel('Multiple requests.');
    cancelTokens.opportunityVoteReasons = CancelToken.source();

    return ApiService.get(`${Config.api.document.url}api/v1/opportunity-vote-reasons`, {
      cancelToken: cancelTokens.opportunityVoteReasons.token,
    }).then((response) => response.data);
  }

  updateOpportunityVote({ opportunityId, voteId, vote, reasons }) {
    return ApiService.put(`${Config.api.document.url}api/v1/opportunities/${opportunityId}/votes/${voteId}`, {
      vote,
      reasons,
    }).then((response) => response.data);
  }

  deleteOpportunityVote({ opportunityId, voteId }) {
    return ApiService.delete(`${Config.api.document.url}api/v1/opportunities/${opportunityId}/votes/${voteId}`).then(
      (response) => response.data,
    );
  }

  addOpportunityVoteReason({ voteType, reason }) {
    return ApiService.post(`${Config.api.document.url}api/v1/opportunity-vote-reasons`, {
      voteType,
      reason,
    }).then((response) => response.data);
  }

  updateOpportunityVoteReason({ id, voteType, reason }) {
    return ApiService.put(`${Config.api.document.url}api/v1/opportunity-vote-reasons/${id}`, {
      voteType,
      reason,
    }).then((response) => response.data);
  }

  deleteOpportunityVoteReason(id) {
    return ApiService.delete(`${Config.api.document.url}api/v1/opportunity-vote-reasons/${id}`).then(
      (response) => response.data,
    );
  }

  /**
   * "No decision recorded" is a 200 with an empty body, so it needs no special handling — a 404
   * from this path means the opportunity itself is unknown. The `|| null` normalizes the empty
   * body, which axios surfaces as `''` rather than `null`.
   */
  getFinalDecision({ opportunityId }) {
    if (cancelTokens.finalDecision) cancelTokens.finalDecision.cancel('Multiple requests.');
    cancelTokens.finalDecision = CancelToken.source();

    return ApiService.get(finalDecisionUrl(opportunityId), {
      cancelToken: cancelTokens.finalDecision.token,
    }).then((response) => response.data || null);
  }

  getFinalDecisionHistory({ opportunityId }) {
    if (cancelTokens.finalDecisionHistory) cancelTokens.finalDecisionHistory.cancel('Multiple requests.');
    cancelTokens.finalDecisionHistory = CancelToken.source();

    return ApiService.get(`${finalDecisionUrl(opportunityId)}/history`, {
      cancelToken: cancelTokens.finalDecisionHistory.token,
    }).then((response) => response.data);
  }

  createFinalDecision({ opportunityId, vote, reasons, comment }) {
    return ApiService.post(finalDecisionUrl(opportunityId), { vote, reasons, comment }).then(
      (response) => response.data,
    );
  }

  updateFinalDecision({ opportunityId, vote, reasons, comment }) {
    return ApiService.put(finalDecisionUrl(opportunityId), { vote, reasons, comment }).then(
      (response) => response.data,
    );
  }

  deleteFinalDecision({ opportunityId }) {
    return ApiService.delete(finalDecisionUrl(opportunityId)).then((response) => response.data);
  }

  createOpportunityVote({ opportunityId, userId, vote, reasons }) {
    return ApiService.post(`${Config.api.document.url}api/v1/opportunities/${opportunityId}/votes`, {
      userId,
      vote,
      reasons,
    }).then((response) => response.data);
  }

  /** An invitation is the same row without a vote yet — the API treats a null vote as pending. */
  inviteUserOpportunityVote({ opportunityId, userId }) {
    return this.createOpportunityVote({ opportunityId, userId, vote: null, reasons: null });
  }
}

export const goNoGoService = new GoNoGoService();
